import { Box, Checkbox } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";

interface ActiveColumnOptions<T> {
	/** `IsActive` on the stops catalogs, `Active` on every other one. */
	field?: string;
	/** Only where the catalog can save the change straight from the grid. */
	onToggle?: (row: T, active: boolean) => void;
}

/**
 * The Active column every configuration catalog shows, in one place: same
 * width, same checkbox, sortable, and filterable through the header funnel.
 *
 * The value is exposed to AG Grid as "Yes" / "No" rather than as a boolean so
 * the set filter lists something a person can read; the checkbox renderer
 * reads the flag off the row instead of off the formatted value.
 */
export const activeColumnDef = <T,>({
	field = "Active",
	onToggle,
}: ActiveColumnOptions<T> = {}): ColDef<T> => ({
	colId: field,
	headerName: "Active",
	flex: 0.5,
	minWidth: 90,
	sortable: true,
	filter: "agSetColumnFilter",
	// The theme hides every header icon, filter funnel included, so a filterable
	// column has to opt its icons back in or nobody can open the filter.
	suppressHeaderMenuButton: false,
	headerClass: "ag-header-with-menu",
	valueGetter: (params) =>
		(params.data as Record<string, unknown> | undefined)?.[field]
			? "Yes"
			: "No",
	cellRenderer: (params: ICellRendererParams<T>) => {
		if (!params.data) return null;
		const active = !!(params.data as Record<string, unknown>)[field];
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
				}}
			>
				<Checkbox
					checked={active}
					size="small"
					disabled={!onToggle}
					onClick={(e) => e.stopPropagation()}
					onChange={
						onToggle
							? (e) => {
									e.stopPropagation();
									onToggle(params.data as T, e.target.checked);
								}
							: undefined
					}
					sx={{ p: 0 }}
				/>
			</Box>
		);
	},
});
